import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import { HybridSearchRetrievalResults } from "@/app/features/diagrams/components/blog/hybridSearchRetrieval/HybridSearchRetrievalResults";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

// Dev-mode topology specification check for referential integrity
const pipelineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "raw-query",
    "taxonomy-prefilter",
    "bm25-index",
    "vector-store",
    "rrf-fusion",
    "ranked-results",
  ],
  junctions: [
    { id: "split-prefilter", kind: "split", x: 350, y: 206 },
    { id: "merge-candidates", kind: "merge", x: 350, y: 316 },
  ],
  connectors: [
    {
      id: "query-to-taxonomy",
      source: { nodeId: "raw-query", anchor: "bottom-center" },
      target: { nodeId: "taxonomy-prefilter", anchor: "top-center" },
      meaning: "Query enters taxonomy pre-filter",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "prefilter-to-split",
      source: { nodeId: "taxonomy-prefilter", anchor: "bottom-center" },
      target: { junctionId: "split-prefilter" },
      meaning: "Filtered query reaches fan-out split point",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "split-to-bm25",
      source: { junctionId: "split-prefilter" },
      target: { nodeId: "bm25-index", anchor: "top-center" },
      meaning: "Exact keyword terms branch",
      semanticType: "keyword",
      lineStyle: "solid",
    },
    {
      id: "split-to-vector",
      source: { junctionId: "split-prefilter" },
      target: { nodeId: "vector-store", anchor: "top-center" },
      meaning: "Semantic similarity branch",
      semanticType: "semantic",
      lineStyle: "dashed",
    },
    {
      id: "bm25-to-merge",
      source: { nodeId: "bm25-index", anchor: "bottom-center" },
      target: { junctionId: "merge-candidates" },
      meaning: "BM25 candidate ranks exit to merge junction",
      semanticType: "keyword",
      lineStyle: "solid",
    },
    {
      id: "vector-to-merge",
      source: { nodeId: "vector-store", anchor: "bottom-center" },
      target: { junctionId: "merge-candidates" },
      meaning: "Vector candidate ranks exit to merge junction",
      semanticType: "semantic",
      lineStyle: "dashed",
    },
    {
      id: "merge-to-rrf",
      source: { junctionId: "merge-candidates" },
      target: { nodeId: "rrf-fusion", anchor: "top-center" },
      meaning: "Merged candidate trunk enters RRF",
      semanticType: "merged",
      lineStyle: "solid",
    },
    {
      id: "rrf-to-output",
      source: { nodeId: "rrf-fusion", anchor: "bottom-center" },
      target: { nodeId: "ranked-results", anchor: "top-center" },
      meaning: "Fused results flow to output",
      semanticType: "success",
      lineStyle: "solid",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(pipelineTopologySpec);
}

export function HybridSearchRetrievalPipelineVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={660}
      height={520}
      title="Pre-filtered Hybrid Search Retrieval Pipeline Architecture"
      description="Pipeline diagram showing a search query pre-filtered by taxonomy metadata constraints before parallel BM25 keyword matching and dense vector search, merged via Reciprocal Rank Fusion into ranked relevant results."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 700 540"
      frame={{
        kind: "boundary",
        label: "PRE-FILTERED RETRIEVAL BOUNDARY",
        x: 20,
        y: 20,
        width: 660,
        height: 500,
      }}
    >
      <DiagramMarkers prefix="hs-" />

      {/* STAGE 1: Search Query (Y: 56 to 100) */}
      <DiagramNode
        x={250}
        y={56}
        width={200}
        height={44}
        label="Search Query"
        sublabel="Text + Meta Filters"
        variant="capsule"
      />

      {/* Stem 1: Search Query -> Taxonomy Pre-Filter (Length = 28px) */}
      <path
        d="M 350 100 L 350 128"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#hs-arrow-neutral)"
      />

      {/* STAGE 2: Taxonomy Pre-Filter (Y: 130 to 178) */}
      <DiagramNode
        x={210}
        y={130}
        width={280}
        height={48}
        label="Taxonomy Pre-Filter"
        sublabel="Metadata Boundaries & Constraints"
        variant="brass"
      />

      {/* Stem down to Split Junction (Length = 28px, M 350 178 -> L 350 206) */}
      <path
        d="M 350 178 L 350 206"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* FAN-OUT CONNECTORS */}
      {/* Left Branch (BM25 Keyword Index) */}
      <path
        d="M 350 206 L 190 206 L 190 234"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.5"
        markerEnd="url(#hs-arrow-neutral)"
      />

      {/* Right Branch (Dense Vector Store) */}
      <path
        d="M 350 206 L 510 206 L 510 234"
        fill="none"
        stroke="#3b82f6"
        strokeWidth="1.6"
        strokeDasharray="5 4"
        opacity="0.85"
        markerEnd="url(#hs-arrow-blue)"
      />

      {/* Split Junction Circle (350, 206) */}
      <circle cx="350" cy="206" r="3.5" fill="var(--ga-brass, #9c6b3e)" />

      {/* Branch Labels (Sitting cleanly 9px above line y=206) */}
      <text
        x="260"
        y="197"
        textAnchor="middle"
        fill="currentColor"
        opacity="0.75"
        className="text-[11px] font-mono font-bold"
      >
        exact terms
      </text>

      <text
        x="440"
        y="197"
        textAnchor="middle"
        fill="#2563eb"
        className="text-[11px] font-mono font-bold"
      >
        semantic similarity
      </text>

      {/* STAGE 3: Parallel Search Engines */}
      <DiagramNode
        x={70}
        y={236}
        width={240}
        height={56}
        label="BM25 Keyword Index"
        sublabel="Exact Codes & Part Numbers"
        variant="standard"
      />

      <DiagramNode
        x={390}
        y={236}
        width={240}
        height={56}
        label="Dense Vector Store"
        sublabel="Semantic Intent & Synonyms"
        variant="llm"
      />

      <HybridSearchRetrievalResults />
    </DiagramFrame>
  );
}
